import { appendBrandIds } from '@/lib/brand-filter-params';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3002';

interface ApiOptions {
    method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
    body?: unknown;
}

async function api<T>(endpoint: string, options: ApiOptions = {}): Promise<T> {
    const { method = 'GET', body } = options;
    const url = `${API_BASE_URL}${endpoint}`;

    const fetchOptions: RequestInit = {
        method,
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
    };

    if (body && method !== 'GET') {
        fetchOptions.body = JSON.stringify(body);
    }

    const response = await fetch(url, fetchOptions);

    if (response.status === 401 || response.status === 403) {
        window.dispatchEvent(new CustomEvent('auth:unauthorized', { detail: { status: response.status } }));
        throw new Error(`Unauthorized (${response.status})`);
    }

    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API error (${response.status}): ${errorText}`);
    }

    return response.json();
}

/** Where a received transfer proof was attributed. */
export type BankTransferSource = 'menteo_order' | 'pos_order' | 'unknown';

/** Persisted verification outcome (`unreadable` never persists — those fall through to OCR). */
export type BankTransferStatus = 'pending' | 'verified' | 'mismatch';

export interface BankTransferExtractedData {
    amount?: number;
    currency?: string;
    recipientAccount?: string;
    recipientName?: string;
    senderName?: string;
    bankName?: string;
    reference?: string;
    date?: string;
}

export interface BankTransferMatchDetails {
    amountMatch?: boolean;
    accountMatch?: boolean;
    nameMatch?: boolean;
}

/**
 * An operator vouched for the payment themselves rather than the receipt reader clearing it — the
 * customer paid in a way this system never saw, or the screenshot on file could not be matched. A
 * `verified` status alone would not say which, so every surface that shows a transfer reads this to
 * tell "comprobante verificado" apart from "el local lo confirmó a mano".
 */
export interface BankTransferManualVerification {
    /** ISO datetime of the override. */
    at: string;
    by: 'operator';
    /** The status the reader had left, or null when the override is what created the transfer. */
    previousStatus: BankTransferStatus | null;
    /** False when nothing was ever attached — the operator's word is the only evidence of payment. */
    hadReceipt: boolean;
    /** What the operator typed: who took the payment, where they saw it, a bank reference. */
    reason?: string;
}

export interface BankTransferVerification {
    extractedData?: BankTransferExtractedData;
    matchDetails?: BankTransferMatchDetails;
    /** Absent when nothing was read off a receipt — i.e. a manual confirmation. */
    confidence?: 'high' | 'medium' | 'low';
    verifiedAt?: string;
    manualVerification?: BankTransferManualVerification;
}

/** Snapshot of a POS order considered (or chosen) when linking a receipt. */
export interface BankTransferPosOrderRef {
    /** Absent when an operator typed the reference by hand. */
    providerType?: string;
    posOrderId: string;
    storeId?: string;
    channel?: string;
    total?: number;
    isPaid?: boolean;
    createdAt?: string;
}

export interface BankTransferLinkMetadata {
    candidates?: BankTransferPosOrderRef[];
    rule?: string;
    relinks?: Array<{
        at: string;
        by: 'operator' | 'agent';
        source: BankTransferSource;
        orderId?: string | null;
        posOrderId?: string | null;
    }>;
}

export interface BankTransfer {
    id: string;
    brandId: string;
    /** Set by aggregate-aware responses so a row can be labeled without a second lookup. */
    brandName?: string;
    locationId?: string | null;
    locationName?: string | null;
    source: BankTransferSource;
    status: BankTransferStatus;
    /** Linked menteo order (source = menteo_order). */
    orderId?: string | null;
    /** Non-null once a newer receipt for the same order superseded this row. */
    supersededAt?: string | null;
    posProviderType?: string;
    posOrderId?: string;
    posStoreId?: string;
    /** Normalized channel of the POS order (store_app | in_store | uber_eats | rappi | didi_food | other). */
    posChannel?: string;
    conversationId?: string;
    customerId?: string;
    customerPhone?: string;
    amount?: number | null;
    currency?: string;
    /** Set when the receipt is archived in S3; fetch a fresh link via getReceiptUrl. */
    receiptKey?: string;
    /** Expiring Gupshup media URL — only useful shortly after the receipt arrived. */
    imageUrl?: string;
    verification?: BankTransferVerification;
    linkMetadata?: BankTransferLinkMetadata;
    /** Operator's free-text note — cross-reference data kept for later reconciliation. */
    note?: string | null;
    createdAt: string;
    updatedAt: string;
}

/** nestjs-typeorm-paginate meta block. */
export interface PaginationMeta {
    totalItems?: number;
    itemCount: number;
    itemsPerPage: number;
    totalPages?: number;
    currentPage: number;
}

interface ApiResponse<T> {
    success: boolean;
    message: string;
    data: T;
}

// Resolves against status.* in the 'bankTransfers' namespace.
export const TRANSFER_STATUS_KEYS: Record<BankTransferStatus, string> = {
    pending: 'pending',
    verified: 'verified',
    mismatch: 'mismatch',
};

export const TRANSFER_STATUS_COLORS: Record<BankTransferStatus, string> = {
    pending: 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300',
    verified: 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300',
    mismatch: 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300',
};

// Resolves against source.* in the 'bankTransfers' namespace.
export const TRANSFER_SOURCE_KEYS: Record<BankTransferSource, string> = {
    menteo_order: 'menteoOrder',
    pos_order: 'posOrder',
    unknown: 'unknown',
};

export const TRANSFER_SOURCE_COLORS: Record<BankTransferSource, string> = {
    menteo_order: 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300',
    pos_order: 'bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-300',
    unknown: 'bg-muted text-muted-foreground',
};

export interface BankTransferListParams {
    from?: string;
    to?: string;
    status?: BankTransferStatus;
    source?: BankTransferSource;
    search?: string;
    page?: number;
    limit?: number;
    /** Aggregate sessions only: narrow to these brands (omitted = every brand in scope). */
    brandIds?: string[];
}

export const bankTransfersClient = {
    async list(params: BankTransferListParams): Promise<ApiResponse<{ items: BankTransfer[]; meta: PaginationMeta }>> {
        const searchParams = new URLSearchParams();
        if (params.from) searchParams.set('from', params.from);
        if (params.to) searchParams.set('to', params.to);
        if (params.status) searchParams.set('status', params.status);
        if (params.source) searchParams.set('source', params.source);
        if (params.search) searchParams.set('search', params.search);
        if (params.page) searchParams.set('page', String(params.page));
        if (params.limit) searchParams.set('limit', String(params.limit));
        appendBrandIds(searchParams, params.brandIds);
        const query = searchParams.toString();
        return api<ApiResponse<{ items: BankTransfer[]; meta: PaginationMeta }>>(
            `/bank-transfers${query ? `?${query}` : ''}`,
        );
    },

    async getById(id: string): Promise<ApiResponse<BankTransfer>> {
        return api<ApiResponse<BankTransfer>>(`/bank-transfers/${id}`);
    },

    /** Short-lived presigned URL for the receipt stored in S3. */
    async getReceiptUrl(id: string): Promise<ApiResponse<{ url: string; expiresIn: number }>> {
        return api<ApiResponse<{ url: string; expiresIn: number }>>(`/bank-transfers/${id}/receipt-url`);
    },

    /**
     * Re-attribute a transfer: `{ orderId }` links a menteo order (null detaches back to unknown);
     * `{ posOrderId, ... }` records a POS reference as typed by the operator (blank store/channel clears them).
     */
    async relink(
        id: string,
        target: {
            orderId?: string | null;
            posOrderId?: string;
            posProviderType?: string;
            posStoreId?: string;
            posChannel?: string;
        },
    ): Promise<ApiResponse<BankTransfer>> {
        return api<ApiResponse<BankTransfer>>(`/bank-transfers/${id}/link`, { method: 'PATCH', body: target });
    },

    /** Save (or clear, with null/blank) the operator note. Never touches attribution — works on any transfer. */
    async setNote(id: string, note: string | null): Promise<ApiResponse<BankTransfer>> {
        return api<ApiResponse<BankTransfer>>(`/bank-transfers/${id}/note`, { method: 'PATCH', body: { note } });
    },
};
